<template>
  <div data-testid="hotel-offer-modal-step-2">
    <div class="tw:pt-6 tw:pb-8">
      <p class="tw:font-bold tw:mb-4">{{ $t('hotel_offers_selected_offer') }}</p>
      <div class="tw:border tw:border-gray-300 tw:bg-white tw:rounded-xl">
        <div class="tw:flex tw:flex-row tw:items-center tw:p-4">
          <div class="tw:flex tw:items-center tw:justify-start tw:gap-x-3 tw:w-full">
            <img class="tw:h-[36px] tw:w-[36px]" :src="icons[offer?.name]" :alt="$t(`ho_${offer?.name}_title`)" />
            <div class="tw:flex tw:flex-col tw:justify-start tw:items-start tw:grow">
              <p class="tw:text-left tw:font-bold">{{ $t(`ho_${offer?.name}_title`) }}</p>
              <p class="tw:text-left tw:text-sm">{{ $t(`ho_${offer?.name}_text`) }}</p>
            </div>
          </div>
        </div>
      </div>
      <p class="tw:text-sm tw:text-gray-600 tw:mt-2">
        {{ $t('hotel_offers_footnote') }}
      </p>
    </div>
    <div v-if="offer.type === 'DISCOUNT'" class="tw:pb-8">
      <p class="tw:font-bold tw:mb-4">{{ $t('hotel_offers_customize_offer') }}</p>
      <div class="tw:flex tw:flex-row tw:items-baseline">
        <div
          class="tw:h-[44px] tw:inline-flex tw:items-center tw:p-1 tw:rounded-md tw:cursor-pointer tw:rounded-lg tw:text-gray-100 tw:border tw:border-gray-300 tw:w-2/3 tw:gap-x-2"
          :class="language === 'fr-FR' ? 'tw:w-2/3' : 'tw:w-1/2'"
        >
          <button
            v-for="offerType in offerTypes"
            :key="offerType.id"
            @click="changeOfferType(offerType.id)"
            class="tw:py-[5px] tw:rounded-lg tw:w-1/2 tw:text-sm"
            :class="
              offerType.id === selectedOfferType
                ? 'tw:bg-blue-200 tw:text-blue-800 tw:font-bold'
                : 'tw:bg-white tw:text-gray-800'
            "
          >
            {{ $t(offerType.label) }}
          </button>
        </div>
        <div
          v-if="selectedOfferType === 1"
          class="tw:ml-4"
          :class="language === 'fr-FR' ? 'tw:w-1/3 tw:min-w-[150px]' : 'tw:w-1/2'"
        >
          <div class="tw:relative tw:h-[44px] tw:w-full tw:flex tw:items-center">
            <BaseTextField
              :placeholder="$t('hotel_offers_discount_percentage')"
              :percentage="true"
              size="big"
              type="number"
              pattern="[0-9]*"
              class="tw:w-full"
              :max="99"
              :min="5"
              :step="1"
              :value="discountValue"
              @input="setDiscountValue"
              @keydown="preventE"
              :error="discountError.length > 0 ? $t(discountError) : false"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="tw:pb-8">
      <div class="tw:flex tw:flex-row tw:items-center tw:mb-4">
        <p class="tw:font-bold">{{ $t('hotel_offers_customize_date') }}</p>
        <UiTooltip :label="$t('hotel_offers_hover_tooltip')" tooltip-position="right" theme="smalldark">
          <InformationOutline class="tw:ml-[10px] tw:fill-none tw:stroke-gray-800 tw:h-[20px] tw:w-[20px]" />
        </UiTooltip>
      </div>
      <div
        class="tw:flex tw:flex-row tw:gap-x-4 tw:min-h-[36px]"
        :class="dateError.length > 0 ? 'tw:items-baseline' : 'tw:items-center'"
      >
        <label v-for="option in dateOptions" :key="option.id" class="tw:mb-0 tw:flex tw:items-center tw:gap-x-2">
          <input
            type="radio"
            :name="option.label"
            :value="selectedDateOption"
            :id="option.id"
            :checked="selectedDateOption === option.id"
            @change="changeDateOption(option.id)"
          />
          <p class="tw:text-sm tw:pb-0">{{ $t(option.label) }}</p>
        </label>
        <div v-if="selectedDateOption === 1">
          <BaseTextField
            id="ho-expiration-date"
            aria-label="Expiration Date"
            type="date"
            :value="expirationDate"
            :min="getMinDate"
            :max="getMaxDate"
            @onChange="changeExpirationDate"
            :error="dateError.length > 0 ? $t(dateError) : false"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import BaseTextField from '@/components/base/inputs/BaseTextField.vue'
import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'
import InformationOutline from '@/components/icons/InformationOutline.svg?component'
import { useSessionValues } from '@/composables/useSessionValues'

defineProps({
  offer: Object,
  icons: Object,
  selectedDateOption: Number,
  dateOptions: Array,
  offerTypes: Array,
  selectedOfferType: Number,
  dateError: [String, null],
  discountError: [String, null],
  discountValue: [Number, null],
  expirationDate: [Date, null],
  getMaxDate: [Date, String],
  getMinDate: [Date, String],
})

const { language } = useSessionValues()
const emit = defineEmits(['changeDateOption', 'changeExpirationDate', 'changeOfferType', 'setDiscountValue'])
const changeDateOption = optionId => emit('changeDateOption', optionId)
const changeExpirationDate = date => emit('changeExpirationDate', date)
const changeOfferType = offerId => emit('changeOfferType', offerId)
const setDiscountValue = e => {
  const value = e.target.value
  emit('setDiscountValue', value)
}

const preventE = e => {
  if (e.key === 'e' || e.key === 'E' || event.key === '+' || event.key === '-') {
    e.preventDefault()
  }
}
</script>
